import { doc, getFirestore, increment, runTransaction, serverTimestamp } from './instrumentedFirestore';
import { getFirebaseApp } from './app';
import { staffDocPath, staffLookupDocPath } from '@constants/firestorePaths';

function db() {
    return getFirestore(getFirebaseApp());
}

function stripUndefined(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    const next = {};
    for (const [k, v] of Object.entries(obj)) {
        if (typeof v === 'undefined') continue;
        next[k] = v;
    }
    return next;
}

export async function upsertTenantStaff({
    tenantId,
    staffId,
    email,
    role = 'superadmin',
    permissions = 'all',
    status = 'active',
    authorities,
}) {
    if (!tenantId) throw new Error('Missing tenantId');
    if (!staffId) throw new Error('Missing staffId');

    const staffRef = doc(db(), staffDocPath(tenantId, staffId));
    const analyticsRef = doc(db(), 'staff', tenantId); // parent analytics doc
    const lookupRef = doc(db(), staffLookupDocPath(staffId));

    await runTransaction(db(), async (tx) => {
        const existing = await tx.get(staffRef);
        const prevStatus = existing.exists() ? existing.data()?.status : null;

        const normalizedStatus = status === 'inactive' ? 'inactive' : 'active';
        const payload = stripUndefined({
            uid: staffId,
            tenantId,
            email,
            role,
            permissions,
            authorities: authorities || undefined,
            status: normalizedStatus,
            updatedAt: serverTimestamp(),
            ...(existing.exists() ? {} : { createdAt: serverTimestamp() }),
        });

        tx.set(staffRef, payload, { merge: true });
        tx.set(
            lookupRef,
            {
                uid: staffId,
                tenantId,
                staffPath: staffRef.path,
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );

        // analytics counters stored at staff/{tenantId}
        if (!existing.exists()) {
            tx.set(
                analyticsRef,
                {
                    tenantId,
                    totalStaff: increment(1),
                    activeStaff: increment(payload.status === 'active' ? 1 : 0),
                    inactiveStaff: increment(payload.status === 'inactive' ? 1 : 0),
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
        } else if (prevStatus && prevStatus !== payload.status) {
            tx.set(
                analyticsRef,
                {
                    activeStaff: increment(payload.status === 'active' ? 1 : -1),
                    inactiveStaff: increment(payload.status === 'inactive' ? 1 : -1),
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );
        } else {
            tx.set(analyticsRef, { updatedAt: serverTimestamp() }, { merge: true });
        }
    });
    return staffId;
}

